import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  ExternalLink,
  Megaphone,
  MessageSquare,
  Paperclip,
  X as CloseIcon,
} from "lucide-react";
import type {
  ReportMessageDetail,
  ReportMessageTimelineEvent,
} from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { ChannelIdentityBadge } from "../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../components/channel/channel-presentation.js";
import { ErrorState } from "../../components/common/ErrorState/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { Button } from "../../components/ui/button/index.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "../../components/ui/sheet/index.js";
import {
  CONTACT_SHEET_MOTION_CLASS,
  CONTACT_SHEET_OVERLAY_MOTION_CLASS,
} from "../contacts/contacts-table-presentation.js";
import { cn } from "../../lib/utils.js";
import {
  copyReportIdentifier,
  detailPrimaryContent,
  presentReportCurrentFailure,
  presentTimelineFailureText,
  reportDetailDirectionLabelKey,
  reportDetailIdentityLabelKey,
  timelineEventLabel,
} from "./message-detail-presentation.js";
import { messageTypeLabelKey } from "./incoming-report-presentation.js";
import {
  formatReportDateTime,
  originBadgeLabel,
  outgoingReportStatusChipClass,
  recipientDisplayPrimary,
  recipientDisplaySecondary,
  statusLabelKey,
} from "./outgoing-report-presentation.js";
import { ReportOriginChip } from "./ReportOriginChip.js";

const DETAIL_SHEET_CLASS =
  "flex h-full max-h-dvh w-full flex-col gap-0 overflow-hidden border-s border-border/60 bg-surface p-0 text-foreground shadow-lg sm:max-w-[28rem] md:max-w-[32rem] lg:max-w-[36rem]";

function timelineNodeClass(event: ReportMessageTimelineEvent): string {
  if (event.errorCode || event.errorMessage || event.status === "FAILED") {
    return "bg-error ring-2 ring-error/20";
  }
  if (
    event.kind === "QUEUED" ||
    event.status === "PENDING" ||
    event.status === "SENDING"
  ) {
    return "bg-warning ring-2 ring-warning/20";
  }
  return "bg-success ring-2 ring-success/20";
}

function DetailChip({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-[0.6875rem] font-medium",
        className ?? "border-line/70 bg-surface-muted/80 text-foreground",
      )}
    >
      {children}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <h2 className="m-0 flex items-center gap-2 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
      <span className="inline-block h-3.5 w-0.5 shrink-0 rounded-full bg-accent/70" aria-hidden="true" />
      {children}
    </h2>
  );
}

function TimelineFailureDetails({
  event,
}: {
  event: Pick<ReportMessageTimelineEvent, "errorCode" | "errorMessage">;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const failureText = presentTimelineFailureText(t, event);
  if (!failureText) return null;
  return (
    <div className="grid min-w-0 gap-0.5">
      <p className="m-0 text-[0.75rem] text-error">{failureText}</p>
      {event.errorCode ? (
        <LtrValue className="text-[0.7rem] text-subtle">{event.errorCode}</LtrValue>
      ) : null}
    </div>
  );
}

export function MessageDetailDrawer({
  open,
  detail,
  loading,
  error,
  onClose,
  onRetry,
}: {
  open: boolean;
  detail: ReportMessageDetail | null;
  loading: boolean;
  error: unknown;
  onClose: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();

  const errorMessage =
    error == null
      ? null
      : isApiError(error) && error.status === 404
        ? t("reports.detail.notFound")
        : isApiError(error) && error.status === 403
          ? t("reports.detail.forbidden")
          : isApiError(error) && error.status === 0
            ? t("reports.detail.backendUnavailable")
            : t("reports.detail.loadFailed");

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="end"
        className={cn(DETAIL_SHEET_CLASS, CONTACT_SHEET_MOTION_CLASS)}
        overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-testid="reports-message-detail-drawer"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border/60 px-5 py-4">
          <div className="grid min-w-0 gap-2">
            <SheetTitle id={titleId} className="m-0 text-start text-[1.0625rem] font-semibold">
              {t("reports.detail.title")}
            </SheetTitle>
            <SheetDescription id={descriptionId} className="sr-only">
              {t("reports.detail.sheetDescription")}
            </SheetDescription>
            {detail ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <DetailChip
                  className={
                    detail.direction === "INBOUND"
                      ? "border-line/70 bg-surface-muted text-muted"
                      : "border-accent/25 bg-accent/10 text-accent"
                  }
                >
                  {t(reportDetailDirectionLabelKey(detail.direction))}
                </DetailChip>
                <DetailChip className="border-line/70 bg-surface text-foreground">
                  <ChannelIdentityBadge
                    platformType={detail.platform}
                    size="filter"
                    label={t(`channel.${channelLabelI18nKey(detail.platform)}`)}
                  />
                  {t(`channel.${channelLabelI18nKey(detail.platform)}`)}
                </DetailChip>
                <DetailChip className={outgoingReportStatusChipClass(detail.status)}>
                  {t(statusLabelKey(detail.status))}
                </DetailChip>
                <DetailChip>
                  <LtrValue className="tabular-nums">
                    {formatReportDateTime(detail.createdAt, i18n.language)}
                  </LtrValue>
                </DetailChip>
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CloseIcon className="size-4" aria-hidden="true" />
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 [scrollbar-gutter:stable]">
          {errorMessage ? (
            <ErrorState layout="panel" message={errorMessage} onRetry={onRetry} retryLabel={t("auth.retry")} />
          ) : null}

          {loading && !detail ? (
            <div className="grid gap-4" aria-busy="true" data-testid="reports-message-detail-loading">
              <div className="h-24 animate-pulse rounded-lg bg-surface-muted" />
              <div className="h-16 animate-pulse rounded-lg bg-surface-muted" />
              <div className="h-40 animate-pulse rounded-lg bg-surface-muted" />
            </div>
          ) : null}

          {detail ? <MessageDetailBody detail={detail} locale={i18n.language} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MessageDetailBody({
  detail,
  locale,
}: {
  detail: ReportMessageDetail;
  locale: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const primary = detailPrimaryContent(detail);
  const identityPrimary = recipientDisplayPrimary(detail.contact);
  const identitySecondary = recipientDisplaySecondary(detail.contact);
  const currentFailurePresentation = detail.currentFailure
    ? presentReportCurrentFailure(
        t,
        detail.currentFailure,
        t("reports.detail.unknownFailure"),
      )
    : null;

  return (
    <div className="grid gap-0" data-testid="reports-message-detail-body">
      <section className="grid gap-2.5 border-b border-border/60 pb-5">
        <SectionLabel>{t("reports.detail.sections.message")}</SectionLabel>
        <div className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-muted">
          <span className="inline-flex items-center gap-1.5 text-foreground">
            <ChannelIdentityBadge
              platformType={detail.platform}
              size="filter"
              label={t(`channel.${channelLabelI18nKey(detail.platform)}`)}
            />
            {t(`channel.${channelLabelI18nKey(detail.platform)}`)}
          </span>
          <span aria-hidden="true">·</span>
          <span>{t(messageTypeLabelKey(detail.messageType))}</span>
        </div>
        {detail.template ? (
          <p className="m-0 text-[0.8125rem] text-muted">
            {t("reports.detail.templateLabel", { name: detail.template.name })}
            {detail.template.languageCode ? ` (${detail.template.languageCode})` : ""}
          </p>
        ) : null}
        {primary ? (
          <div className="rounded-lg border border-line/70 bg-surface-muted/50 px-3.5 py-3">
            <p className="m-0 whitespace-pre-wrap break-words text-[0.9375rem] leading-relaxed text-foreground">
              {primary}
            </p>
          </div>
        ) : (
          <p className="m-0 text-[0.8125rem] text-muted">{t("reports.detail.noContent")}</p>
        )}
      </section>

      <section className="grid gap-2.5 border-b border-border/60 py-5">
        <SectionLabel>{t(reportDetailIdentityLabelKey(detail.direction))}</SectionLabel>
        <p className="m-0 text-[0.9375rem] font-medium text-foreground">{identityPrimary}</p>
        {identitySecondary ? (
          <p className="m-0 text-[0.8125rem] text-muted">
            <LtrValue>{identitySecondary}</LtrValue>
          </p>
        ) : null}
        {detail.contact.contactId == null ? (
          <p className="m-0 text-[0.75rem] text-muted">{t("reports.detail.unsavedIdentity")}</p>
        ) : null}
      </section>

      <section className="grid gap-2.5 border-b border-border/60 py-5">
        <SectionLabel>{t("reports.detail.sections.context")}</SectionLabel>
        <dl className="m-0 grid gap-2 text-[0.8125rem]">
          <DetailRow
            label={t("reports.detail.fields.account")}
            value={detail.endpoint.displayLabel ?? detail.endpoint.externalEndpointId}
          />
          {detail.direction === "OUTBOUND" ? (
            <div className="grid gap-0.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center sm:gap-3">
              <dt className="m-0 text-muted">{t("reports.detail.fields.origin")}</dt>
              <dd className="m-0 min-w-0">
                {detail.origin ? (
                  <ReportOriginChip
                    origin={detail.origin}
                    label={originBadgeLabel(t, detail.origin)}
                  />
                ) : (
                  t("reports.detail.emDash")
                )}
              </dd>
            </div>
          ) : null}
          {detail.campaign ? (
            <div className="grid gap-1">
              <DetailRow
                label={t("reports.detail.fields.campaign")}
                value={
                  detail.campaign.relation === "CAMPAIGN_SEND_TEST"
                    ? t("reports.detail.sendTestCampaign", {
                        name: detail.campaign.campaignName || `#${detail.campaign.campaignId}`,
                      })
                    : detail.campaign.campaignName || `#${detail.campaign.campaignId}`
                }
              />
              {detail.links.campaignPath ? (
                <Link
                  to={detail.links.campaignPath}
                  className="inline-flex items-center gap-1 text-accent no-underline hover:underline"
                >
                  <Megaphone size={12} aria-hidden="true" />
                  {t("reports.detail.viewCampaign")}
                </Link>
              ) : null}
            </div>
          ) : null}
          {detail.links.conversationPath ? (
            <Link
              to={detail.links.conversationPath}
              className="inline-flex items-center gap-1 text-accent no-underline hover:underline"
            >
              <MessageSquare size={12} aria-hidden="true" />
              {t("reports.detail.openConversation")}
              <ExternalLink size={11} aria-hidden="true" />
            </Link>
          ) : null}
        </dl>
      </section>

      <section className="grid gap-3 border-b border-border/60 py-5">
        <SectionLabel>{t("reports.detail.sections.lifecycle")}</SectionLabel>
        <ol className="m-0 grid list-none gap-0 p-0">
          {detail.timeline.map((event, index) => (
            <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
              <span className="relative mt-1.5 flex flex-col items-center">
                <span
                  className={cn("size-2.5 shrink-0 rounded-full", timelineNodeClass(event))}
                  aria-hidden="true"
                />
                {index < detail.timeline.length - 1 ? (
                  <span className="mt-1 w-px flex-1 bg-line/80" aria-hidden="true" />
                ) : null}
              </span>
              <div className="min-w-0 flex-1 grid gap-0.5">
                <p className="m-0 text-[0.875rem] font-medium text-foreground">
                  {timelineEventLabel(t, event, detail.direction)}
                  {event.duplicateCount > 1 ? ` ×${event.duplicateCount}` : ""}
                </p>
                <p className="m-0 text-[0.75rem] text-muted">
                  {formatReportDateTime(event.occurredAt, locale)}
                </p>
                <TimelineFailureDetails event={event} />
              </div>
            </li>
          ))}
        </ol>
      </section>

      {detail.currentFailure ? (
        <section className="grid gap-2 border-b border-border/60 py-5">
          <SectionLabel>
            <span className="text-error">{t("reports.detail.sections.failure")}</span>
          </SectionLabel>
          <div className="rounded-lg border border-error/30 bg-error-muted/40 px-3 py-2.5">
            {currentFailurePresentation ? (
              <>
                <p className="m-0 text-[0.875rem] text-foreground">
                  {currentFailurePresentation.primary}
                </p>
                {currentFailurePresentation.secondary ? (
                  <p className="m-0 mt-1 text-[0.75rem] text-muted">
                    {currentFailurePresentation.secondary}
                  </p>
                ) : null}
                {currentFailurePresentation.technicalCode ? (
                  <LtrValue className="mt-1 block text-[0.7rem] text-subtle">
                    {currentFailurePresentation.technicalCode}
                  </LtrValue>
                ) : null}
              </>
            ) : null}
            {detail.currentFailure.occurredAt ? (
              <p className="m-0 mt-1 text-[0.75rem] text-muted">
                {formatReportDateTime(detail.currentFailure.occurredAt, locale)}
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {detail.attachments.length > 0 ? (
        <section className="grid gap-2 border-b border-border/60 py-5">
          <SectionLabel>{t("reports.detail.sections.attachments")}</SectionLabel>
          <ul className="m-0 grid list-none gap-2 p-0">
            {detail.attachments.map((attachment) => (
              <li
                key={attachment.attachmentId}
                className="flex items-start gap-2 rounded-md border border-line/70 px-3 py-2 text-[0.8125rem]"
              >
                <Paperclip size={14} className="mt-0.5 shrink-0 text-muted" aria-hidden="true" />
                <div className="min-w-0 grid gap-0.5">
                  <span className="font-medium">
                    {attachment.fileName ?? attachment.attachmentType}
                  </span>
                  <span className="text-muted">
                    {[attachment.mimeType, attachment.fileSizeBytes, attachment.storageState]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <details className="group py-5">
        <summary className="cursor-pointer list-none text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-ink-subtle marker:content-none [&::-webkit-details-marker]:hidden">
          <span className="inline-flex items-center gap-2">
            {t("reports.detail.sections.technical")}
            <span className="text-[0.75rem] font-normal normal-case tracking-normal text-muted group-open:hidden">
              …
            </span>
          </span>
        </summary>
        <dl className="mt-3 grid gap-2 text-[0.8125rem]">
          <CopyableId label={t("reports.detail.fields.messageId")} value={detail.technical.messageId} />
          {detail.technical.externalMessageId ? (
            <CopyableId
              label={t("reports.detail.fields.providerMessageId")}
              value={detail.technical.externalMessageId}
            />
          ) : null}
          <DetailRow
            label={t("reports.detail.fields.conversationId")}
            value={String(detail.technical.conversationId)}
          />
          <DetailRow
            label={t("reports.detail.fields.endpointId")}
            value={String(detail.technical.endpointId)}
          />
          {detail.technical.campaignId != null ? (
            <DetailRow
              label={t("reports.detail.fields.campaignId")}
              value={String(detail.technical.campaignId)}
            />
          ) : null}
          {detail.technical.campaignRecipientId ? (
            <DetailRow
              label={t("reports.detail.fields.campaignRecipientId")}
              value={detail.technical.campaignRecipientId}
            />
          ) : null}
          {detail.technical.origin ? (
            <DetailRow
              label={t("reports.detail.fields.origin")}
              value={detail.technical.origin}
            />
          ) : null}
          {detail.technical.sendJobStatus ? (
            <DetailRow
              label={t("reports.detail.fields.sendJob")}
              value={detail.technical.sendJobStatus}
            />
          ) : null}
        </dl>
      </details>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
      <dt className="m-0 text-muted">{label}</dt>
      <dd className="m-0 min-w-0 break-words text-foreground">{value}</dd>
    </div>
  );
}

function CopyableId({ label, value }: { label: string; value: string }): React.JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid gap-0.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center sm:gap-3">
      <dt className="m-0 text-muted">{label}</dt>
      <dd className="m-0 flex min-w-0 items-center gap-2">
        <LtrValue className="min-w-0 truncate font-mono text-[0.75rem]">{value}</LtrValue>
        <Button
          type="button"
          variant="ghost"
          className="size-7 shrink-0 p-0 text-muted hover:text-foreground"
          aria-label={t("reports.detail.copyId", { label })}
          onClick={() => {
            void copyReportIdentifier(value).then((ok) => {
              if (!ok) return;
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1200);
            });
          }}
        >
          {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
        </Button>
      </dd>
    </div>
  );
}
